import StearsDashboard from "@/public/assets/img/profile-completion-dashboard.png"
import { ToastPosition } from "@/src/common/components/Toast/Toast"
import { useToastContext } from "@/src/common/hooks/useToast/ToastProvider"
import { HIDE_PROFILE_COMPLETION_PROMPT } from "@/src/sb/constants"
import { ONBOARDING_WIZARD_V2 } from "@/src/sb/constants/featureFlags"
import { SBAPIInstance } from "@/src/sb/data-layer"
import { useUser } from "@/src/sb/hooks/useUser"
import { useFeatureIsOn } from "@growthbook/growthbook-react"
import classNames from "classnames"
import { useSession } from "next-auth/react"
import Image from "next/image"
import { useEffect, useState } from "react"

import ArrowRightSmall from "../../icon/ArrowRightSmall"
import X from "../../icon/X"
import ProfileCompletionWizard, {
  COMPLETION_STEP_PANEL_CLASSES,
  ProfileCompletionData
} from "./ProfileCompletionWizard"
import { needsProfileCompletion } from "./needsProfileCompletion"
import { parseProfileApiError } from "./profileApiErrors"
import { buildProfileCompletionPayload } from "./profilePayload"
import {
  isProfileCompletionPromptDismissed,
  rememberProfileCompletionPromptDismissed
} from "./promptDismissal"

const COPY = {
  title: "Help us make Stears work better for you.",
  body: "We want to better understand your workflow and how you use Stears. Your answers will help us build a better experience around the way you work.",
  duration: "It takes less than a minute to complete.",
  start: "Start",
  saved: "Thank you! Your response has been submitted."
}

const TOAST_POSITION: ToastPosition = "top-right"

const ProfileCompletionPrompt = () => {
  const { loading, data: user } = useUser()
  const { data: session, update: refreshSession } = useSession()
  const showToastMessage = useToastContext()
  const wizardEnabled = useFeatureIsOn(ONBOARDING_WIZARD_V2)
  const [dismissed, setDismissed] = useState(true)
  const [started, setStarted] = useState(false)
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Array<string>>([])

  useEffect(() => {
    setDismissed(isProfileCompletionPromptDismissed())
  }, [])

  const dismiss = () => {
    rememberProfileCompletionPromptDismissed()
    setDismissed(true)
  }

  const handleComplete = async (data: ProfileCompletionData) => {
    setErrors([])
    setSaving(true)

    try {
      await SBAPIInstance.updateMemberProfile(
        buildProfileCompletionPayload(data) as any,
        (session?.user as any)?.accessToken
      )
    } catch (error) {
      const { formErrors, fieldErrors } = parseProfileApiError(error)
      setErrors([...formErrors, ...Object.values(fieldErrors)])
      throw new Error("Could not save profile")
    } finally {
      setSaving(false)
    }

    setStarted(false)
    dismiss()
    showToastMessage({
      type: "success",
      title: COPY.saved,
      position: TOAST_POSITION
    })

    refreshSession().catch(() => undefined)
  }

  if (!wizardEnabled || loading || dismissed || !needsProfileCompletion(user)) {
    return null
  }

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-label={COPY.title}
      className="dark fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-stears_black/70 p-2 font-jakarta"
    >
      <div
        className={classNames(
          "relative flex w-full max-h-[calc(100vh-16px)] border border-[#4B5563] bg-[#1A1D21]",
          {
            [COMPLETION_STEP_PANEL_CLASSES[step]]: started,
            "max-w-[620px] overflow-auto md:h-[271px]": !started
          }
        )}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="absolute right-5 top-5 z-10 flex h-5 w-5 items-center justify-center bg-gray-600 text-stears_white"
        >
          <X size={20} />
        </button>

        {started ? (
          <div className="w-full p-5">
            {errors.length ? (
              <div
                role="alert"
                className="mb-4 flex flex-col gap-1 bg-[#1A1D21] p-2 text-xs text-[#F05042]"
              >
                {errors.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            ) : null}
            <ProfileCompletionWizard
              storageKey={`${HIDE_PROFILE_COMPLETION_PROMPT}:${
                (user as { user_id?: number })?.user_id ?? "member"
              }`}
              onComplete={handleComplete}
              onBack={() => setStarted(false)}
              onStepChange={setStep}
            />
          </div>
        ) : (
          <>
            <div className="flex w-full min-w-0 flex-col p-5 md:w-1/2">
              <div className="flex w-full max-w-[270px] flex-1 flex-col justify-between gap-6">
                <div className="flex h-[159px] w-full flex-col gap-[15px]">
                  <h2 className="font-sans text-base font-bold leading-6 tracking-normal text-stears_white">
                    {COPY.title}
                  </h2>
                  <div className="flex flex-col gap-4 text-xs tracking-[-0.01em] text-[#9CA3AF]">
                    <p className="font-sans text-xs font-normal leading-4 tracking-[-0.01em] text-[#9CA3AF]">
                      {COPY.body}
                    </p>
                    <p>{COPY.duration}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1)
                    setStarted(true)
                  }}
                  disabled={saving}
                  className="flex h-[38px] w-[92px] items-center justify-center gap-2 whitespace-nowrap bg-[#4B5563] px-5 py-2.5 text-stears_white disabled:opacity-60"
                >
                  <span className="flex h-4 w-7 shrink-0 items-center font-sans text-xs font-normal leading-4 tracking-normal">
                    {COPY.start}
                  </span>
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    <ArrowRightSmall size={16} />
                  </span>
                </button>
              </div>
            </div>

            <div className="relative hidden w-1/2 shrink-0 md:block">
              <Image
                src={StearsDashboard}
                alt=""
                aria-hidden
                fill
                className="object-cover object-left"
                priority
              />
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default ProfileCompletionPrompt
